<template>
    <lni-disclosure
        :id="`${id}_accordion`"
        class="--accordion"
        @toggle="onToggle"
    >
        <template slot="indicator">
            <span
                aria-hidden="true"
                class="lnicon--caret--right lni-u-type--lg lni-u-ma0 lni-u-text--center"
            />
        </template>
        <span
            slot="toggle"
            class="lni-u-heading--2 lni-u-pl1"
        >
            {{ content?.workPaceHeading }}
        </span>
        <template slot="content">
            <div class="--row lni-u-gap2 lni-u-mt2">
                <div class="lni-c-box--content --card --col__1of4">
                    <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                        {{ content?.workPaceTotalHours }}
                    </p>
                    <span class="--label --number">
                        {{ hoursPlannedTotal }}
                    </span>
                </div>
                <div class="lni-c-box--content --card --col__1of4">
                    <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                        {{ content?.workPaceSuggestedHours }}
                    </p>
                    <span class="--label --number">
                        {{ hoursStandardTotal }}
                    </span>
                </div>
                <div class="lni-c-box--content --card --col__2of4">
                    <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                        {{ content?.workPaceTimePrefix }}
                    </p>
                    <span
                        v-if="plannedRate < 1"
                        class="--label"
                    >
                        {{ content?.workPaceTimeInsufficient }}
                    </span>
                    <span
                        v-else
                        class="--label"
                    >
                        {{ content?.workPaceTimeSufficient }}
                    </span>
                    <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mt1">
                        {{ content?.workPaceTimeText }}
                        <span
                            v-if="plannedRate < 1"
                            class="lni-u-type--bold"
                        >
                            {{ content?.lessText }}
                        </span>
                        <span
                            v-else
                            class="lni-u-type--bold"
                        >
                            {{ content?.moreText }}
                        </span>
                        {{ content?.workPaceTimePostfix }}
                    </p>
                </div>
            </div>
            <lni-janitorial-complaint-card
                v-if="isManagementReport || isErgonomicEvaluation"
                :id="`${id}_complaint`"
                classString="lni-u-mt2"
                :complaint="paceComplaint"
            >
                <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mb1">
                    {{ content?.complaintPrefix }}
                </p>
                <span class="--label">
                    {{ paceComplaint
                        ? content?.workPaceComplaint
                        : content?.workPaceText }}
                </span>
                <p class="lni-u-type--xs lni-u-line-height--tight lni-u-mt1">
                    {{ content?.workPacePostfix }}
                </p>
            </lni-janitorial-complaint-card>
            <div class="lni-u-mt3">
                <h3 class="lni-u-heading--3 lni-u-mb1">
                    {{ content?.workPaceCalculationHeading }}
                </h3>
                <div class="lni-u-reading-width--max">
                    {{ content?.workPaceTableDescription }}
                </div>
            </div>
            <lni-table
                :id="`${id}_table`"
                class="lni-u-mb3"
                :hidePagingActions="true"
                :caption="content?.workPaceTableCaption"
                :summary="content?.workPaceTableSummary"
                :striped="true"
                :items="items"
                loadState="loaded"
                :sort="{
                    index: 'asc',
                }"
                :columns="columns"
            >
                <template
                    slot="body"
                    slot-scope="props"
                >
                    <tr>
                        <td class="lni-u-text--center">
                            {{ props.item.index }}
                        </td>
                        <td class="lni-u-text--left">
                            {{ props.item.assignment }}
                        </td>
                        <td class="lni-u-text--center">
                            {{ props.item.hoursPlanned }}
                        </td>
                        <td class="lni-u-text--center">
                            {{ props.item.hoursStandard }}
                        </td>
                    </tr>
                </template>
            </lni-table>
        </template>
    </lni-disclosure>
</template>

<script>
import {
    mapGetters,
} from 'vuex';

import {
    hoursPlanned,
    hoursPlannedTotal,
    hoursStandard,
    hoursStandardTotal,
    plannedRate,
} from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/calculations';

import {
    assignmentText,
    timeByHours,
} from '@gov.wa.lni/application.janitorial-workload-calculator/source/logic/helpers';

import LniJanitorialComplaintCard from '@gov.wa.lni/application.janitorial-workload-calculator/source/components/shared/lni-janitorial-complaint-card.component.vue';

export default {
    name: 'LniJanitorialWorkPace',
    components: {
        LniJanitorialComplaintCard,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        jobPlan: {
            type: Array,
            required: true,
        },
        paceComplaint: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['toggle'],
    computed: {
        columns() {
            const result = [
                {
                    source: 'hoursPlanned',
                    displayText: this.content?.hoursPlannedText,
                },
                {
                    source: 'hoursStandard',
                    displayText: this.content?.hoursStandardText,
                },
            ];

            return this.tableColumns(result);
        },
        hoursPlannedTotal() {
            return timeByHours(hoursPlannedTotal(this.jobPlan), 0);
        },
        hoursStandardTotal() {
            return timeByHours(hoursStandardTotal(this.jobPlan), 0);
        },
        items() {
            return this.jobPlan.map((assignment, index) => ({
                index: index + 1,
                assignment: assignmentText(assignment),
                hoursPlanned: hoursPlanned(assignment),
                hoursStandard: timeByHours(hoursStandard(assignment), 0),
            }));
        },
        plannedRate() {
            return plannedRate(this.hoursPlannedTotal, this.hoursStandardTotal);
        },
        ...mapGetters({
            content: 'appState/content',
            isErgonomicEvaluation: 'appState/isErgonomicEvaluation',
            isManagementReport: 'appState/isManagementReport',
            tableColumns: 'appState/tableColumns',
        }),
    },
    mounted() {
        this.$nextTick(() => {
            this.$store.commit('setAttribute', {
                id: `${this.id}_table`,
                attribute: 'items',
                value: this.items,
            }, {
                root: true,
            });
            this.$store.commit('setAttribute', {
                id: `${this.id}_table`,
                attribute: 'totalItems',
                value: this.items.length,
            }, {
                root: true,
            });
        });
    },
    methods: {
        onToggle() {
            this.$emit('toggle');
        },
    },
};
</script>